import { api } from '@/clients/admin.client';
import { Project, Tenant } from '@/clients/types';
import { ActionIcon, Menu, Table } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDots, IconEdit } from '@tabler/icons-react';
import { useRouter } from '@tanstack/react-router';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { fmtCurrency } from '@/lib/currency';
import { UpdateLimitForm, UpdateLimitInputs } from './UpdateLimitForm';

type Props = {
  tenant: Tenant;
  projects: Project[];
  adminClient: typeof api;
};

export function ProjectUsageLimitsTable({ tenant, projects, adminClient }: Props) {
  const router = useRouter();
  const currency = tenant.spec.billing_settings.currency;
  // 0 は未設定を意味するので、金額としては表示しない
  const fmtMonthlyBill = (v: number | null | undefined) =>
    v ? `${fmtCurrency(v, currency)} / 月` : null;

  const updateProject = async (project: Project, inputs: UpdateLimitInputs) => {
    try {
      await adminClient.admin_v1_update_project(
        {
          usage_limits: {
            alert_monthly_bill: inputs.usageAlert,
            limit_monthly_bill: inputs.usageLimit,
          },
        },
        {
          params: { project_id: project.keys.id! },
        }
      );
      await router.load();
      notifySuccess('テナント設定を更新しました。');
    } catch (e) {
      logger.error(e);
      notifyError('テナント設定の更新に失敗しました。');
    }
  };

  const openUpdateModal = (project: Project) => {
    modals.open({
      size: 'md',
      title: 'テナント利用上限設定',
      children: (
        <UpdateLimitForm
          currency={currency}
          onSubmit={(inputs) => updateProject(project, inputs)}
          defaultValues={{
            usageAlert: project.spec.usage_limits?.alert_monthly_bill ?? 0,
            usageLimit: project.spec.usage_limits?.limit_monthly_bill ?? 0,
          }}
        />
      ),
    });
  };

  return (
    <Table.ScrollContainer minWidth={620} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th></Table.Th>
            <Table.Th>予算アラート</Table.Th>
            <Table.Th>予算上限（ハードリミット）</Table.Th>
            <Table.Th w="10%"></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {projects.map((project) => (
            <Table.Tr key={project.keys.id}>
              <Table.Td data-label="プロジェクト">{project.spec.name}</Table.Td>
              <Table.Td data-label="予算アラート">
                {fmtMonthlyBill(project.spec.usage_limits?.alert_monthly_bill) ?? '未設定'}
              </Table.Td>
              <Table.Td data-label="予算上限（ハードリミット）">
                {fmtMonthlyBill(project.spec.usage_limits?.limit_monthly_bill) ?? '未設定'}
              </Table.Td>
              <Table.Td data-action>
                <Menu>
                  <Menu.Target>
                    <ActionIcon variant="outline" size="md">
                      <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit style={{ width: '70%', height: '70%' }} />}
                      onClick={() => {
                        openUpdateModal(project);
                      }}
                    >
                      設定
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
